import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '../config';
import { BackendResponse } from '../../types/interfaces';
import { deserializeResponse, replaceUrlParams } from '../utils/helpers';
import { Observable, map, catchError, throwError, tap, of } from 'rxjs';
import { Department } from '../../../shared/models/department.model';
import {
  Resident,
  ResidentPayload,
  UpdateResident,
} from '../../../shared/models/resident.model';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '../utils/snackbar.service';
import { AuthenticationService } from '../authentication';
import { TestCustomerService } from '../test-customer/test-customer.service';
import { ResponseStatus } from '../../types/enums';

@Injectable({
  providedIn: 'root',
})
export class ResidentsService {
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private snackbarService: SnackbarService,
    private translateService: TranslateService,
    private testCustomerService: TestCustomerService
  ) {}
  residentsOrdersSum: {
    beginningOfMonth: { [key: number]: number };
    middleOfMonth: { [key: number]: number };
  } = {
    beginningOfMonth: {},
    middleOfMonth: {},
  };

  getResident(residentId: number): Observable<BackendResponse<Resident>> {
    if (
      this.testCustomerService.isLoggedInAsTestCustomer() &&
      this.testCustomerService.hasResident(residentId)
    ) {
      const stored = this.testCustomerService.getResident(residentId);

      if (stored) {
        return of({
          data: stored,
          status: ResponseStatus.Success,
          message: 'Successfully retrieved resident for test customer',
        } as BackendResponse<Resident>);
      }

      return throwError(() => new Error('RESIDENT_NOT_FOUND'));
    }
    const url = replaceUrlParams(
      this.configService.endpoints.residents.getResident,
      ['residentId'],
      [residentId]
    );
    return this.http.get<BackendResponse<Resident>>(url).pipe(
      map(res => deserializeResponse(res, Resident)),
      catchError(error => {
        if (error.isHandled) return;
        this.snackbarService.error(
          this.translateService.instant('residents.error.inResidentRetrieving')
        );
        return throwError(() => error);
      }),
      tap(resident => {
        if (this.testCustomerService.isLoggedInAsTestCustomer()) {
          this.testCustomerService.saveResident(resident.data);
        }
      })
    );
  }

  updateResident(data: Resident) {
    if (this.testCustomerService.isLoggedInAsTestCustomer()) {
      this.testCustomerService.saveResident(data);
      return of({
        status: ResponseStatus.Success,
        message: 'Resident updated successfully for test customer',
      });
    }
    const url = replaceUrlParams(
      this.configService.endpoints.residents.updateResident,
      ['residentId'],
      [data.id]
    );
    return this.http.put(url, data.mapResidentToSave(true)).pipe(
      map(res => res),
      catchError(error => {
        if (error.isHandled) return;
        this.snackbarService.error(
          this.translateService.instant('residents.error.inResidentUpdating')
        );
        return throwError(() => error);
      })
    );
  }

  deleteResident(residentId: number) {
    if (this.testCustomerService.isLoggedInAsTestCustomer()) {
      this.testCustomerService.removeResident(residentId);
      return of({
        status: ResponseStatus.Success,
        message: 'Resident deleted successfully for test customer',
      });
    }
    const url = replaceUrlParams(
      this.configService.endpoints.residents.deleteResident,
      ['residentId'],
      [residentId]
    );
    return this.http.delete(url).pipe(
      map(res => res),
      catchError(error => {
        if (error.isHandled) return;
        this.snackbarService.error(
          this.translateService.instant('residents.error.inResidentRemoving')
        );
        return throwError(() => error);
      })
    );
  }

  createResident(data: Resident) {
    if (this.testCustomerService.isLoggedInAsTestCustomer()) {
      const residentId = this.testCustomerService.saveResident(data);
      return of({
        status: ResponseStatus.Success,
        message: 'Resident created successfully for test customer',
        data: residentId,
      });
    }
    const url = this.configService.endpoints.residents.createResident;
    return this.http.post(url, data.mapResidentToSave()).pipe(
      map(res => res),
      catchError(error => {
        if (error.isHandled) return;
        this.snackbarService.error(
          this.translateService.instant('residents.error.inResidentCreating')
        );
        return throwError(() => error);
      })
    );
  }

  getResidentOrdersSum(customerId: string) {
    const url = replaceUrlParams(
      this.configService.endpoints.residents.getResidentOrdersSum,
      ['customerId'],
      [customerId]
    );
    return this.http.get(url).pipe(
      map(res => res),
      tap(res => {
        this.residentsOrdersSum = (<any>res).data;
      }),
      catchError(error => {
        if (error.isHandled) return;
        this.snackbarService.error(
          this.translateService.instant(
            'residents.error.inResidentOrderSumRetrieving'
          )
        );
        return throwError(() => error);
      })
    );
  }
}
